import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useChatStore } from '../stores/chat-store';
import ChatMessage from './ChatMessage';

interface ChatPanelProps {
  surface?: 'app' | 'marketing';
}

const ChatPanel: React.FC<ChatPanelProps> = ({ surface = 'app' }) => {
  const { messages, isLoading, sendMessage, toggleChat, connectionFailed, retryConnection } = useChatStore();

  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const welcomeMessage =
    surface === 'marketing'
      ? "Hi! Have a question about StockClerk? I'm here to help."
      : "Hi there! How can I help you today?";

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Focus trap
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggleChat();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [toggleChat]
  );

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    sendMessage(trimmed);
    setInput('');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      data-testid="chat-panel"
      aria-label="Support chat"
      onKeyDown={handleKeyDown}
      className="fixed bottom-6 right-6 z-50 w-96 h-[500px] bg-white rounded-xl shadow-2xl border border-bronze-200 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-bronze-200 bg-background-alt">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
            C
          </div>
          <span className="font-medium text-text">Clerk</span>
        </div>
        <button
          onClick={toggleChat}
          aria-label="Close chat"
          className="p-1 text-text-muted hover:text-text rounded transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Message List */}
      <div
        role="log"
        aria-live="polite"
        className="flex-1 overflow-y-auto px-4 py-3 custom-scrollbar"
      >
        {messages.length === 0 && (
          <div className="flex justify-start mb-3">
            <div className="max-w-[80%] rounded-lg px-3 py-2 text-sm bg-background-alt text-text rounded-bl-none">
              <p>{welcomeMessage}</p>
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} surface={surface} />
        ))}
        {isLoading && (
          <div data-testid="chat-loading" role="status" className="flex justify-start mb-3">
            <div className="bg-background-alt rounded-lg px-3 py-2 text-sm text-text-muted rounded-bl-none">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        {connectionFailed && (
          <div className="flex justify-center my-3">
            <button
              onClick={() => retryConnection(surface)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors"
            >
              Retry Connection
            </button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-bronze-200">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            role="textbox"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            aria-label="Type a message"
            placeholder="Ask Clerk anything..."
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-bronze-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary bg-white text-text placeholder-text-muted"
          />
          <button
            onClick={handleSend}
            disabled={isLoading}
            aria-label="Send message"
            className="p-2 rounded-lg bg-primary text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
